import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Package, Plus, Edit, Trash2, Search, Filter,
  CheckCircle, XCircle, ChevronLeft, ChevronRight,
  Info, AlertTriangle, X
} from 'lucide-react';
import toast from 'react-hot-toast';
import featureService from '../services/featureService';
import { useDebounce } from '../hooks/useDebounce';
import { CreateButton, SaveButton, UpdateButton, DeleteButton, CancelButton } from '../components/CommonButtons';
import Switch from '../components/Switch';
import CommonLoader, { TableLoader } from '../components/CommonLoader';
import FilterBar from '../components/FilterBar';
import StandardDataTable from '../components/StandardDataTable';
import ResponsiveDataTable from '../components/ResponsiveDataTable';
import EnhancedDataTable from '../components/EnhancedDataTableV2';
import EnhancedModal from '../components/EnhancedModal';

const Features = () => {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [totalFeatures, setTotalFeatures] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: 1
  });
  const [formErrors, setFormErrors] = useState({});

  // Debounce search term
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  useEffect(() => {
    fetchFeatures();
  }, [currentPage, debouncedSearchTerm, filterActive, perPage, sortBy, sortOrder]);

  const fetchFeatures = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        per_page: perPage,
        search: debouncedSearchTerm,
        is_active: filterActive,
        sort_by: sortBy,
        sort_order: sortOrder
      };

      const response = await featureService.getFeatures(params);
      
      // Handle response structure
      const featuresList = response.features || response.data || [];
      
      setFeatures(featuresList);
      
      if (response.pagination) {
        setTotalFeatures(response.pagination.total || 0);
        setTotalPages(response.pagination.last_page || 1);
        setCurrentPage(response.pagination.current_page || 1);
      }
    } catch (error) {
      toast.error('Failed to fetch features');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Feature name is required';
    }
    
    if (!formData.description.trim()) {
      errors.description = 'Feature description is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      // Ensure is_active is sent as 0 or 1
      const submitData = {
        ...formData,
        is_active: formData.is_active ? 1 : 0
      };
      
      if (selectedFeature) {
        await featureService.updateFeature(selectedFeature.id, submitData);
        toast.success('Feature updated successfully');
      } else {
        await featureService.createFeature(submitData);
        toast.success('Feature created successfully');
      }
      
      setShowModal(false);
      resetForm();
      fetchFeatures();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save feature');
    }
  };

  const handleEdit = (feature) => {
    setSelectedFeature(feature);
    setFormData({
      name: feature.name,
      description: feature.description || '',
      is_active: feature.is_active ? 1 : 0
    });
    setShowModal(true);
  };

  const handleDelete = async () => {
    try {
      await featureService.deleteFeature(selectedFeature.id);
      toast.success('Feature deleted successfully');
      setShowDeleteModal(false);
      setSelectedFeature(null);
      fetchFeatures();
    } catch (error) {
      toast.error('Failed to delete feature');
    }
  };


  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      is_active: 1
    });
    setFormErrors({});
    setSelectedFeature(null);
  };

  const handleToggleStatus = async (featureId) => {
    try {
      // Find the feature to get current status
      const feature = features.find(f => f.id === featureId);
      if (!feature) return;

      // Toggle the status
      const newStatus = feature.is_active ? 0 : 1;

      // Send only the required fields for update
      await featureService.updateFeature(featureId, {
        name: feature.name,
        description: feature.description,
        is_active: newStatus
      });

      toast.success(`Feature ${newStatus ? 'activated' : 'deactivated'} successfully`);
      fetchFeatures();
    } catch (error) {
      console.error('Toggle status error:', error);
      toast.error('Failed to update feature status');
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Features Management</h1>
        <p className="mt-1 text-sm text-gray-600">
          Manage features that can be assigned to pricing plans
        </p>
      </div>

      {/* Header Actions */}
      <div className="flex flex-col lg:flex-row gap-4 justify-between mb-6">
          <div className="flex flex-col lg:flex-row gap-4 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search features..."
                className="w-full pl-10 pr-4 py-1.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>

            <select
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value)}
              className="px-3 py-1.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="">All Features</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>

            <select
              value={perPage}
              onChange={(e) => setPerPage(e.target.value)}
              className="px-3 py-1.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="15">15 per page</option>
              <option value="30">30 per page</option>
              <option value="50">50 per page</option>
            </select>
          </div>

          <CreateButton
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            size="sm"
          >
            Add Feature
          </CreateButton>
      </div>

      {/* Features Table */}
      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        <EnhancedDataTable
          data={features}
          columns={[
            {
              key: 'name',
              header: 'Feature Name',
              sortable: true,
              render: (value, row) => (
                <div className="flex items-center">
                  <Package className="h-5 w-5 text-gray-400 mr-3" />
                  <div className="text-sm font-medium text-gray-900">
                    {row.name}
                  </div>
                </div>
              )
            },
            {
              key: 'description',
              header: 'Description',
              sortable: false,
              render: (value) => (
                <div className="text-sm text-gray-500">
                  {value || 'No description'}
                </div>
              )
            },
            {
              key: 'is_active',
              header: 'Status',
              sortable: true,
              render: (value, feature) => (
                <Switch
                  checked={feature.is_active === 1 || feature.is_active === true}
                  onChange={() => handleToggleStatus(feature.id)}
                  tooltip={feature.is_active ? 'Click to deactivate' : 'Click to activate'}
                />
              )
            },
            {
              key: 'created_at',
              header: 'Created',
              sortable: true,
              render: (value) => (
                <span className="text-sm text-gray-500">
                  {new Date(value).toLocaleDateString()}
                </span>
              )
            }
          ]}
          actions={(row) => [
            {
              icon: Edit,
              label: 'Edit',
              onClick: () => handleEdit(row)
            },
            {
              icon: Trash2,
              label: 'Delete',
              onClick: () => {
                setSelectedFeature(row);
                setShowDeleteModal(true);
              },
              className: 'text-red-600 hover:text-red-800'
            }
          ]}
          loading={loading}
          emptyMessage="No features found"
          emptyIcon={Package}
          showColumnControls={true}
          enableColumnResize={true}
          enableDragDrop={true}
          onSort={(key, direction) => {
            const sorted = [...features].sort((a, b) => {
              let aVal = a[key];
              let bVal = b[key];
              
              if (key === 'created_at') {
                aVal = aVal ? new Date(aVal).getTime() : 0;
                bVal = bVal ? new Date(bVal).getTime() : 0;
              }
              
              if (aVal < bVal) return direction === 'asc' ? -1 : 1;
              if (aVal > bVal) return direction === 'asc' ? 1 : -1;
              return 0;
            });
            setFeatures(sorted);
          }}
        />

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed focus:border-transparent"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed focus:border-transparent"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{(currentPage - 1) * perPage + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * perPage, totalFeatures)}
                  </span>{' '}
                  of <span className="font-medium">{totalFeatures}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-3 py-1.5 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-3 py-1.5 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <EnhancedModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          resetForm();
        }}
        title={selectedFeature ? 'Edit Feature' : 'Create New Feature'}
        size="medium"
        closeOnEscape={true}
        closeOnOutsideClick={true}
        showCloseButton={true}
      >
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Feature Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                formErrors.name ? 'border-red-500' : ''
              }`}
              placeholder="e.g., Advanced Analytics"
            />
            {formErrors.name && (
              <p className="mt-1 text-sm text-red-500">{formErrors.name}</p>
            )}
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                formErrors.description ? 'border-red-500' : ''
              }`}
              placeholder="Enter feature description..."
            />
            {formErrors.description && (
              <p className="mt-1 text-sm text-red-500">{formErrors.description}</p>
            )}
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <Switch
              checked={formData.is_active === 1}
              onChange={(value) => setFormData({ ...formData, is_active: value ? 1 : 0 })}
              label={formData.is_active === 1 ? 'Active Feature' : 'Inactive Feature'}
            />
            <p className="mt-1 text-xs text-gray-500">
              Active features can be assigned to pricing plans
            </p>
          </div>

          <div className="flex justify-end space-x-3">
            <CancelButton
              type="button"
              onClick={() => {
                setShowModal(false);
                resetForm();
              }}
            />
            {selectedFeature ? (
              <UpdateButton type="submit">
                Update Feature
              </UpdateButton>
            ) : (
              <SaveButton type="submit">
                Create Feature
              </SaveButton>
            )}
          </div>
        </form>
      </EnhancedModal>

      {/* Delete Confirmation Modal */}
      <EnhancedModal
        isOpen={showDeleteModal && selectedFeature}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedFeature(null);
        }}
        title="Delete Feature"
        size="medium"
        closeOnEscape={true}
        closeOnOutsideClick={true}
        showCloseButton={true}
      >
        <div className="flex items-center mb-4">
          <AlertTriangle className="h-6 w-6 text-red-600 mr-2" />
          <span className="text-lg font-semibold">Confirm Deletion</span>
        </div>
        
        <p className="text-gray-600 mb-4">
          Are you sure you want to delete the feature "{selectedFeature?.name}"? This action cannot be undone and will remove the feature from all pricing plans.
        </p>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
          <div className="flex">
            <Info className="h-5 w-5 text-yellow-400 mr-2 flex-shrink-0" />
            <p className="text-sm text-yellow-800">
              This feature may be associated with existing pricing plans. Deleting it will remove it from all plans.
            </p>
          </div>
        </div>

        <div className="flex justify-end space-x-3">
          <CancelButton
            onClick={() => {
              setShowDeleteModal(false);
              setSelectedFeature(null);
            }}
          />
          <DeleteButton
            onClick={handleDelete}
          >
            Delete Feature
          </DeleteButton>
        </div>
      </EnhancedModal>
    </div>
  );
};

export default Features;